import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { useToast } from '../../../shared/hooks/use-toast';

export type CatalogueState<Row> = { status: 'loading' } | { status: 'loaded'; rows: Row[] } | { status: 'error' };

export interface CatalogueApi<Row, Input> {
  list: () => Promise<Row[]>;
  create: (input: Input) => Promise<unknown>;
  update: (id: string, input: Input) => Promise<unknown>;
  deactivate: (id: string) => Promise<void>;
  reactivate: (id: string) => Promise<void>;
}

interface CatalogueOptions<Input> {
  /** Shown inline in the drawer when the server answers 409 (the code is taken). */
  conflictMessage: string;
  /** A server-side 400 can say more than the generic line, e.g. which field was wrong. */
  badRequestMessage?: (err: ApiError, input: Input) => string | null;
}

/**
 * The list-and-mutate hook the cargo catalogues share (container sizes and types, weight bands): create and update keep their
 * error inline in the drawer, deactivate and reactivate toast. `reason-codes` has the same shape, with its own copy.
 */
export function useCatalogue<Row extends { name: string }, Input extends { name: string }>(api: CatalogueApi<Row, Input>, options: CatalogueOptions<Input>) {
  const { state, reload } = useCatalogueRows(api.list);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(input: Input, action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(toSaveError(err, input, options));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  return {
    state,
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    create: (input: Input) => save(input, () => api.create(input), `${input.name} created`),
    update: (id: string, input: Input) => save(input, () => api.update(id, input), `${input.name} updated`),
    deactivate: (row: Row & { id: string }) => rowAction(() => api.deactivate(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`),
    reactivate: (row: Row & { id: string }) => rowAction(() => api.reactivate(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`),
  };
}

function useCatalogueRows<Row>(list: () => Promise<Row[]>) {
  const [state, setState] = useState<CatalogueState<Row>>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    list()
      .then((rows) => {
        if (!cancelled) setState({ status: 'loaded', rows });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [list]);

  /** Re-fetches after a mutation commits. */
  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', rows: await list() });
    } catch {
      setState({ status: 'error' });
    }
  }, [list]);

  return { state, reload };
}

function toSaveError<Input>(err: unknown, input: Input, { conflictMessage, badRequestMessage }: CatalogueOptions<Input>): string {
  if (err instanceof ApiError && err.status === 409) return conflictMessage;
  if (err instanceof ApiError && err.status === 400) return badRequestMessage?.(err, input) ?? GENERIC_SAVE_ERROR;
  return GENERIC_SAVE_ERROR;
}

const GENERIC_SAVE_ERROR = 'Could not save — check the fields and try again.';
